import { useState } from "react";
import { Badge, Button, Card, ErrorNotice, Input, Spinner } from "../components/ui.tsx";
import { api } from "../lib/api.ts";
import { useAction, useQuery } from "../lib/hooks.ts";
import { useT } from "../lib/i18n.tsx";
import type { IpBloccato, TentativiIp } from "../lib/types.ts";
import { formatoData } from "./Stacks.tsx";

interface Accessi {
  tentativi: TentativiIp[];
  bloccati: IpBloccato[];
  tuoIp: string | null;
}

/**
 * Accessi falliti per indirizzo, e la blacklist che li ferma.
 *
 * Si blocca l'indirizzo, non lo username: quello lo sceglie chi attacca.
 */
export function SezioneAccessi() {
  const { data, error, loading, reload } = useQuery<Accessi>("/impostazioni/accessi");
  const t = useT();
  const bloccati = new Set(data?.bloccati.map((b) => b.ip));

  return (
    <section className="space-y-6">
      <div>
        <h2 className="font-medium">{t.t("accessi.titolo")}</h2>
        <p className="text-sm tenue">{t.t("accessi.intro")}</p>
      </div>

      <ErrorNotice error={error} />
      {loading && !data && <Spinner className="tenue" />}

      {data && (
        <>
          <div className="space-y-2">
            <h3 className="text-sm font-medium">{t.t("accessi.tentativi")}</h3>
            {data.tentativi.length === 0 ? (
              <Card className="px-4 py-6 text-center text-sm tenue">{t.t("accessi.nessunTentativo")}</Card>
            ) : (
              <div className="overflow-x-auto rounded-xl border">
                <table className="w-full text-sm">
                  <thead className="superficie text-left text-xs tenue">
                    <tr>
                      <th className="px-4 py-2 font-medium">{t.t("accessi.colIp")}</th>
                      <th className="px-4 py-2 font-medium">{t.t("accessi.colFalliti")}</th>
                      <th className="hidden px-4 py-2 font-medium sm:table-cell">{t.t("accessi.colUtenti")}</th>
                      <th className="hidden px-4 py-2 font-medium sm:table-cell">{t.t("accessi.colUltimo")}</th>
                      <th className="px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {data.tentativi.map((r) => (
                      <RigaTentativi
                        key={r.ip}
                        riga={r}
                        bloccato={bloccati.has(r.ip)}
                        tuo={r.ip === data.tuoIp}
                        onCambiato={reload}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <h3 className="text-sm font-medium">{t.t("accessi.bloccati")}</h3>
            <FormBlocco onBloccato={reload} />
            {data.bloccati.length === 0 ? (
              <Card className="px-4 py-6 text-center text-sm tenue">{t.t("accessi.nessunBloccato")}</Card>
            ) : (
              <Card className="divide-y overflow-hidden">
                {data.bloccati.map((b) => (
                  <RigaBloccato key={b.ip} bloccato={b} onCambiato={reload} />
                ))}
              </Card>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function RigaTentativi({
  riga,
  bloccato,
  tuo,
  onCambiato,
}: {
  riga: TentativiIp;
  bloccato: boolean;
  tuo: boolean;
  onCambiato: () => void;
}) {
  const t = useT();
  const azione = useAction(async () => {
    if (bloccato) await api.delete(`/impostazioni/accessi/bloccati/${encodeURIComponent(riga.ip)}`);
    else await api.put("/impostazioni/accessi/bloccati", { ip: riga.ip });
    onCambiato();
  });
  const utenti = riga.utenti.slice(0, 3).join(", ") + (riga.utenti.length > 3 ? ` +${riga.utenti.length - 3}` : "");

  return (
    <tr className="border-t align-top">
      <td className="px-4 py-2.5">
        <code className="font-mono text-[13px]">{riga.ip}</code>
        <div className="mt-1 flex flex-wrap gap-1">
          {bloccato && <Badge tone="danger">{t.t("accessi.bloccato")}</Badge>}
          {tuo && <Badge>{t.t("accessi.tuoIp")}</Badge>}
        </div>
        <ErrorNotice error={azione.error} />
      </td>
      <td className="px-4 py-2.5">
        {riga.falliti}
        {riga.riusciti > 0 && (
          <p className="text-xs text-amber-600" title={t.t("accessi.riuscitiAiuto")}>
            {t.t("accessi.riusciti", { n: riga.riusciti })}
          </p>
        )}
      </td>
      <td className="hidden max-w-56 truncate px-4 py-2.5 font-mono text-xs sm:table-cell" title={riga.utenti.join(", ")}>
        {utenti}
      </td>
      <td className="hidden px-4 py-2.5 text-xs tenue sm:table-cell">{formatoData(riga.ultimo, t)}</td>
      <td className="px-4 py-2.5 text-right">
        {!tuo && (
          <Button
            variant={bloccato ? "secondary" : "danger"}
            className="px-2.5 py-1 text-xs"
            loading={azione.running}
            onClick={() => void azione.run()}
          >
            {t.t(bloccato ? "accessi.sblocca" : "accessi.blocca")}
          </Button>
        )}
      </td>
    </tr>
  );
}

function RigaBloccato({ bloccato, onCambiato }: { bloccato: IpBloccato; onCambiato: () => void }) {
  const t = useT();
  const sblocco = useAction(async () => {
    await api.delete(`/impostazioni/accessi/bloccati/${encodeURIComponent(bloccato.ip)}`);
    onCambiato();
  });

  return (
    <div className="space-y-2 px-4 py-3">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <code className="font-mono text-[13px]">{bloccato.ip}</code>
        {bloccato.motivo && <span className="tenue">{bloccato.motivo}</span>}
        <span className="ml-auto flex items-center gap-3">
          <span className="hidden text-xs tenue sm:inline">
            {t.t("accessi.bloccatoDa", { utente: bloccato.createdBy, quando: formatoData(bloccato.createdAt, t) })}
          </span>
          <Button variant="ghost" className="text-xs" loading={sblocco.running} onClick={() => void sblocco.run()}>
            {t.t("accessi.sblocca")}
          </Button>
        </span>
      </div>
      <ErrorNotice error={sblocco.error} />
    </div>
  );
}

/** Per bloccare un indirizzo visto altrove, ad esempio nei log del proxy. */
function FormBlocco({ onBloccato }: { onBloccato: () => void }) {
  const t = useT();
  const [ip, setIp] = useState("");
  const [motivo, setMotivo] = useState("");
  const blocco = useAction(async () => {
    await api.put("/impostazioni/accessi/bloccati", { ip, motivo });
    setIp("");
    setMotivo("");
    onBloccato();
  });

  return (
    <form
      className="space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        void blocco.run();
      }}
    >
      <div className="flex flex-wrap gap-2">
        <Input
          className="w-48 font-mono"
          placeholder={t.t("accessi.ipSegnaposto")}
          aria-label={t.t("accessi.colIp")}
          value={ip}
          onChange={(e) => setIp(e.target.value)}
        />
        <Input
          className="min-w-0 flex-1"
          placeholder={t.t("accessi.motivo")}
          aria-label={t.t("accessi.motivo")}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
        />
        <Button type="submit" variant="danger" disabled={!ip.trim()} loading={blocco.running}>
          {t.t("accessi.blocca")}
        </Button>
      </div>
      <ErrorNotice error={blocco.error} />
    </form>
  );
}
